import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Check, CheckCircle2, Clock, Copy, Eye, FileDown, Link2, Loader2, Mail, MapPin,
  Plus, Send, Trash2, UserPlus, Users, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePdfExport } from '@/hooks/usePdfExport';
import { cn } from '@/lib/utils';

const API = API_BASE;

const STATUS = {
  pending:   { label: 'Not sent',  cls: 'text-muted-foreground' },
  sent:      { label: 'Sent',      cls: 'text-blue-600 dark:text-blue-300' },
  opened:    { label: 'Opened',    cls: 'text-amber-600 dark:text-amber-300' },
  confirmed: { label: 'Confirmed', cls: 'text-green-600 dark:text-green-300' },
  failed:    { label: 'Failed',    cls: 'text-destructive' },
};

const Field = ({ label, children, className }) => (
  <div className={cn('space-y-1', className)}>
    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

/**
 * Call sheet editor (M3.1) for one shoot day: header fields, recipients with
 * individual calls, preview, PDF, private links and send (M3.2/3.3).
 * Route: /workspaces/:workspaceId/films/:filmId/days/:scheduleId/call-sheet
 */
export default function CallSheetPage() {
  const { workspaceId, filmId, scheduleId } = useParams();
  const navigate = useNavigate();
  const [film, setFilm] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirtyRecipients, setDirtyRecipients] = useState(false);
  const [people, setPeople] = useState({ cast: [], crew: [] });
  const [addOpen, setAddOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendMsg, setSendMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [links, setLinks] = useState(null);
  const [tab, setTab] = useState('edit'); // edit | preview
  const [preview, setPreview] = useState(null);
  const { exportPdf, exporting } = usePdfExport();
  const saveTimer = useRef(null);

  const canEdit = film?.can_edit !== false;
  const boardUrl = workspaceId ? `/workspaces/${workspaceId}/films/${filmId}/board` : `/films/${filmId}/board`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [f, cs] = await Promise.all([
        axios.get(`${API}/films/${filmId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/films/${filmId}/schedules/${scheduleId}/call-sheet`, { headers: getAuthHeaders() }),
      ]);
      setFilm(f.data);
      let data = cs.data;
      if (!data.call_sheet) {
        if (f.data?.can_edit === false) { setError('No call sheet has been created for this day yet.'); setLoading(false); return; }
        const gen = await axios.post(`${API}/films/${filmId}/schedules/${scheduleId}/call-sheet`, {}, { headers: getAuthHeaders() });
        data = gen.data;
        toast.success('Call sheet drafted from the day', { description: 'Check calls and location, then send.' });
      }
      setSheet(data.call_sheet);
      setRecipients(data.recipients || []);
      axios.get(`${API}/films/${filmId}/cast`, { headers: getAuthHeaders() }).then((r) => setPeople((p) => ({ ...p, cast: r.data || [] }))).catch(() => {});
      axios.get(`${API}/films/${filmId}/crew`, { headers: getAuthHeaders() }).then((r) => setPeople((p) => ({ ...p, crew: r.data || [] }))).catch(() => {});
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not load the call sheet');
    } finally {
      setLoading(false);
    }
  }, [filmId, scheduleId]);

  useEffect(() => { load(); }, [load]);

  /* ── sheet fields: debounced autosave ── */
  const patchSheet = (patch) => {
    setSheet((s) => ({ ...s, ...patch }));
    if (!canEdit) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await axios.put(`${API}/films/${filmId}/call-sheets/${sheet.id}`, patch, { headers: getAuthHeaders() });
        setSheet((s) => ({ ...s, version: data.call_sheet.version, status: data.call_sheet.status }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not save');
      } finally {
        setSaving(false);
      }
    }, 600);
  };

  /* ── recipients ── */
  const updRecipient = (id, patch) => {
    setRecipients((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setDirtyRecipients(true);
  };
  const removeRecipient = (id) => { setRecipients((rs) => rs.filter((r) => r.id !== id)); setDirtyRecipients(true); };
  const addPerson = (p, type) => {
    if (recipients.some((r) => r.person_id === p.id)) { toast.message(`${p.name} is already on the sheet`); return; }
    setRecipients((rs) => [...rs, {
      id: `new-${p.id}`, person_type: type, person_id: p.id, name: p.name, role: p.role || '',
      department: type === 'cast' ? 'Cast' : undefined, phone: p.phone || '', email: p.email || '',
      call_time: type === 'cast' ? sheet?.general_call : sheet?.crew_call, pickup_time: null, notes: '', scenes: [],
      included: true, status: 'pending',
    }]);
    setDirtyRecipients(true);
  };
  const saveRecipients = async () => {
    setSaving(true);
    try {
      const body = { recipients: recipients.map((r, i) => ({
        ...(r.id && !String(r.id).startsWith('new-') ? { id: r.id } : {}),
        person_type: r.person_type, person_id: r.person_id, name: r.name, role: r.role, department: r.department,
        phone: r.phone, email: r.email, call_time: r.call_time, pickup_time: r.pickup_time, notes: r.notes,
        scenes: r.scenes || [], sort_order: i, included: r.included !== false,
      })) };
      const { data } = await axios.put(`${API}/films/${filmId}/call-sheets/${sheet.id}/recipients`, body, { headers: getAuthHeaders() });
      setRecipients(data.recipients);
      setDirtyRecipients(false);
      toast.success('Recipients saved');
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save recipients');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const groups = useMemo(() => {
    const g = {};
    recipients.forEach((r) => { (g[r.department || 'Other'] = g[r.department || 'Other'] || []).push(r); });
    const order = ['Cast', 'Direction', 'Production', 'Camera', 'Lighting', 'Grip', 'Sound', 'Art', 'Costume', 'Makeup & Hair', 'Stunts', 'VFX', 'SFX', 'Transport', 'Catering', 'Post', 'Other'];
    return Object.entries(g).sort((a, b) => (order.indexOf(a[0]) === -1 ? 99 : order.indexOf(a[0])) - (order.indexOf(b[0]) === -1 ? 99 : order.indexOf(b[0])));
  }, [recipients]);

  const included = recipients.filter((r) => r.included !== false);
  const withEmail = included.filter((r) => (r.email || '').trim());
  const confirmed = included.filter((r) => r.status === 'confirmed').length;

  /* ── preview / pdf / links / send ── */
  const loadPreview = async () => {
    if (dirtyRecipients) await saveRecipients();
    try {
      const { data } = await axios.get(`${API}/films/${filmId}/call-sheets/${sheet.id}/preview`, { headers: getAuthHeaders() });
      setPreview(data);
      setTab('preview');
    } catch (err) { toast.error('Could not build the preview'); }
  };
  const downloadPdf = async () => {
    if (dirtyRecipients) await saveRecipients();
    exportPdf(`/films/${filmId}/call-sheets/${sheet.id}/export/call-sheet.pdf`, { kind: 'call_sheet', fallbackName: 'Call sheet.pdf' });
  };
  const makeLinks = async () => {
    if (dirtyRecipients && !(await saveRecipients())) return;
    try {
      const { data } = await axios.post(`${API}/films/${filmId}/call-sheets/${sheet.id}/links`, {}, { headers: getAuthHeaders() });
      setLinks(data.links);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not create links'); }
  };
  const copyAllLinks = async () => {
    const text = (links || []).map((l) => `${l.name}: ${l.url}`).join('\n');
    try { await navigator.clipboard.writeText(text); toast.success('Links copied'); } catch { toast.error('Copy failed'); }
  };
  const send = async () => {
    if (dirtyRecipients && !(await saveRecipients())) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/films/${filmId}/call-sheets/${sheet.id}/send`, { channel: 'email', message: sendMsg || null }, { headers: getAuthHeaders() });
      setSendResult(data);
      setSheet((s) => ({ ...s, status: 'sent', sent_at: new Date().toISOString() }));
      const cs = await axios.get(`${API}/films/${filmId}/schedules/${scheduleId}/call-sheet`, { headers: getAuthHeaders() });
      setRecipients(cs.data.recipients || []);
      toast.success(`Call sheet sent to ${data.sent} by email${data.links_only ? `, ${data.links_only} by link` : ''}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="p-6 space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-2xl bg-muted/50 animate-pulse" />)}</div>;
  if (error || !sheet) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Link to={boardUrl} className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-4"><ArrowLeft className="size-3" /> Back to the board</Link>
        <div className="rounded-2xl border border-border p-8 text-center text-sm text-muted-foreground">{error || 'No call sheet.'}</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="call-sheet-page">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Link to={boardUrl} className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-1 hover:text-foreground"><ArrowLeft className="size-3" /> Board</Link>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight">{sheet.title || 'Call sheet'}</h1>
          <p className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2">
            <span>v{sheet.version || 1}</span>
            <span>·</span>
            <span className={sheet.status === 'sent' ? 'text-green-600 dark:text-green-300' : ''}>{sheet.status === 'sent' ? `Sent · ${confirmed}/${included.length} confirmed` : 'Draft — not sent yet'}</span>
            {saving && <span className="inline-flex items-center gap-1"><Loader2 className="size-3 animate-spin" /> saving</span>}
            {dirtyRecipients && !saving && <span className="text-amber-600">unsaved recipient changes</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40">
            <button type="button" onClick={() => setTab('edit')} className={cn('px-2.5 h-7 rounded-md text-xs font-medium', tab === 'edit' ? 'bg-background shadow-sm' : 'text-muted-foreground')}>Edit</button>
            <button type="button" onClick={loadPreview} className={cn('px-2.5 h-7 rounded-md text-xs font-medium inline-flex items-center gap-1', tab === 'preview' ? 'bg-background shadow-sm' : 'text-muted-foreground')}><Eye className="size-3" /> Preview</button>
          </div>
          <Button size="sm" variant="outline" className="h-8" onClick={downloadPdf} disabled={exporting} data-testid="cs-pdf"><FileDown className="size-4 mr-1" /> PDF</Button>
          {canEdit && <Button size="sm" variant="outline" className="h-8" onClick={makeLinks} data-testid="cs-links"><Link2 className="size-4 mr-1" /> Links</Button>}
          {canEdit && (
            <Button size="sm" className="h-8" onClick={() => { setSendResult(null); setSendOpen(true); }} disabled={included.length === 0} data-testid="cs-send">
              <Send className="size-4 mr-1" /> {sheet.status === 'sent' ? 'Re-send' : 'Send'}
            </Button>
          )}
        </div>
      </div>

      {tab === 'preview' && preview && <PreviewPane view={preview} />}

      {tab === 'edit' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-5">
          {/* Sheet fields */}
          <div className="space-y-4">
            <section className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-2"><Clock className="size-3.5" /> Calls</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[['general_call', 'General'], ['crew_call', 'Crew'], ['shooting_call', 'Shooting'], ['est_wrap', 'Est. wrap']].map(([k, l]) => (
                  <Field key={k} label={l}><Input value={sheet[k] || ''} onChange={(e) => patchSheet({ [k]: e.target.value })} placeholder="07:00" className="h-9 font-semibold" disabled={!canEdit} data-testid={`cs-${k}`} /></Field>
                ))}
              </div>
              <Field label="Shoot date"><Input type="date" value={(sheet.shoot_date || '').slice(0, 10)} onChange={(e) => patchSheet({ shoot_date: e.target.value })} className="h-9 w-48" disabled={!canEdit} /></Field>
            </section>

            <section className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-2"><MapPin className="size-3.5" /> Location</h2>
              <Field label="Location"><Input value={sheet.location_name || ''} onChange={(e) => patchSheet({ location_name: e.target.value })} className="h-9" disabled={!canEdit} /></Field>
              <Field label="Address"><Input value={sheet.location_address || ''} onChange={(e) => patchSheet({ location_address: e.target.value })} className="h-9" disabled={!canEdit} /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Field label="Map link"><Input value={sheet.map_url || ''} onChange={(e) => patchSheet({ map_url: e.target.value })} placeholder="https://maps.app.goo.gl/…" className="h-9" disabled={!canEdit} /></Field>
                <Field label="Parking / unit base"><Input value={sheet.parking_notes || ''} onChange={(e) => patchSheet({ parking_notes: e.target.value })} className="h-9" disabled={!canEdit} /></Field>
              </div>
              <Field label="Nearest hospital"><Input value={sheet.nearest_hospital || ''} onChange={(e) => patchSheet({ nearest_hospital: e.target.value })} placeholder="Name, area, phone" className="h-9" disabled={!canEdit} /></Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Weather"><Input value={sheet.weather || ''} onChange={(e) => patchSheet({ weather: e.target.value })} className="h-9" disabled={!canEdit} /></Field>
                <Field label="Sunrise"><Input value={sheet.sunrise || ''} onChange={(e) => patchSheet({ sunrise: e.target.value })} className="h-9" disabled={!canEdit} /></Field>
                <Field label="Sunset"><Input value={sheet.sunset || ''} onChange={(e) => patchSheet({ sunset: e.target.value })} className="h-9" disabled={!canEdit} /></Field>
              </div>
            </section>

            <section className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Key contacts</h2>
              {(sheet.contacts || []).map((c, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={c.name || ''} placeholder="Name" className="h-8 text-xs" disabled={!canEdit} onChange={(e) => patchSheet({ contacts: sheet.contacts.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                  <Input value={c.role || ''} placeholder="Role" className="h-8 text-xs" disabled={!canEdit} onChange={(e) => patchSheet({ contacts: sheet.contacts.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)) })} />
                  <Input value={c.phone || ''} placeholder="Phone" className="h-8 text-xs" disabled={!canEdit} onChange={(e) => patchSheet({ contacts: sheet.contacts.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)) })} />
                  {canEdit && <button type="button" onClick={() => patchSheet({ contacts: sheet.contacts.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive"><X className="size-4" /></button>}
                </div>
              ))}
              {canEdit && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => patchSheet({ contacts: [...(sheet.contacts || []), { name: '', role: '', phone: '' }] })}><Plus className="size-3 mr-1" /> Add contact</Button>}
            </section>

            <section className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <Field label="Notes for everyone"><Textarea value={sheet.notes || ''} onChange={(e) => patchSheet({ notes: e.target.value })} rows={3} disabled={!canEdit} data-testid="cs-notes" /></Field>
              <Field label="Advance — next day"><Textarea value={sheet.advance_notes || ''} onChange={(e) => patchSheet({ advance_notes: e.target.value })} rows={2} disabled={!canEdit} /></Field>
            </section>
          </div>

          {/* Recipients */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-2"><Users className="size-3.5" /> Who's called</h2>
              <span className="text-xs text-muted-foreground">{included.length} people · {withEmail.length} with email</span>
              <div className="ml-auto flex gap-2">
                {canEdit && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAddOpen(true)}><UserPlus className="size-3.5 mr-1" /> Add</Button>}
                {canEdit && dirtyRecipients && <Button size="sm" className="h-7 text-xs" onClick={saveRecipients} disabled={saving} data-testid="cs-save-recipients"><Check className="size-3.5 mr-1" /> Save</Button>}
              </div>
            </div>
            {groups.map(([dept, rows]) => (
              <section key={dept} className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">{dept} <span className="font-normal">({rows.length})</span></div>
                <div className="divide-y divide-border">
                  {rows.map((r) => {
                    const st = STATUS[r.status] || STATUS.pending;
                    return (
                      <div key={r.id} className={cn('grid grid-cols-[auto_4.5rem_4.5rem_minmax(0,1fr)] items-center gap-2 px-3 py-1.5', r.included === false && 'opacity-50')} data-testid={`cs-recipient-${r.id}`}>
                        <Checkbox checked={r.included !== false} onCheckedChange={(v) => updRecipient(r.id, { included: !!v })} disabled={!canEdit} title="Called today" />
                        <Input value={r.call_time || ''} onChange={(e) => updRecipient(r.id, { call_time: e.target.value })} placeholder="Call" className="h-8 text-xs font-semibold px-2" disabled={!canEdit} />
                        <Input value={r.pickup_time || ''} onChange={(e) => updRecipient(r.id, { pickup_time: e.target.value || null })} placeholder="Pickup" className="h-8 text-xs px-2" disabled={!canEdit} />
                        <div className="min-w-0 flex items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{r.name} {r.role && <span className="text-muted-foreground font-normal text-xs">· {r.role}</span>}</div>
                            <div className="text-[11px] text-muted-foreground truncate flex flex-wrap gap-x-2">
                              {r.scenes?.length > 0 && <span>Sc {r.scenes.join(', ')}</span>}
                              <span className={cn(r.email ? '' : 'text-amber-600')}>{r.email || 'no email — link only'}</span>
                              {r.phone && <span>{r.phone}</span>}
                              <span className={st.cls}>{st.label}{r.status === 'confirmed' && <CheckCircle2 className="size-3 inline ml-0.5" />}</span>
                            </div>
                          </div>
                          {canEdit && <button type="button" onClick={() => removeRecipient(r.id)} className="text-muted-foreground hover:text-destructive shrink-0" aria-label={`Remove ${r.name}`}><Trash2 className="size-3.5" /></button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
            {recipients.length === 0 && <div className="rounded-2xl border border-dashed border-border p-6 text-center text-xs text-muted-foreground">No one is called yet. Tag cast on scenes and add crew to the film, or add people here.</div>}
          </div>
        </div>
      )}

      {/* Links panel */}
      {links && (
        <section className="rounded-2xl border border-border bg-card p-4 space-y-2" data-testid="cs-links-panel">
          <div className="flex items-center gap-2">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Private links</h2>
            <span className="text-xs text-muted-foreground">one per person — paste into your WhatsApp group until WhatsApp sending is on</span>
            <div className="ml-auto flex gap-1">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={copyAllLinks}><Copy className="size-3.5 mr-1" /> Copy all</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setLinks(null)}>Close</Button>
            </div>
          </div>
          <ul className="text-xs divide-y divide-border max-h-64 overflow-y-auto">
            {links.map((l) => (
              <li key={l.recipient_id} className="py-1.5 flex items-center gap-2">
                <span className="w-40 truncate font-medium">{l.name}</span>
                <code className="flex-1 truncate text-muted-foreground">{l.url}</code>
                <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => navigator.clipboard?.writeText(l.url).then(() => toast.success('Copied'))}><Copy className="size-3.5" /></button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Add person dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add to the call sheet</DialogTitle><DialogDescription>Cast and crew on this film who aren't on today's sheet.</DialogDescription></DialogHeader>
          <div className="max-h-72 overflow-y-auto divide-y divide-border">
            {[['cast', people.cast], ['crew', people.crew]].map(([type, list]) => list.filter((p) => !recipients.some((r) => r.person_id === p.id)).map((p) => (
              <button key={p.id} type="button" onClick={() => { addPerson(p, type); }} className="w-full text-left px-2 py-2 hover:bg-accent/40 flex items-center gap-2 text-sm">
                <Plus className="size-3.5 text-muted-foreground" /><span className="font-medium">{p.name}</span><span className="text-xs text-muted-foreground">{p.role} · {type}</span>
              </button>
            )))}
            {people.cast.length + people.crew.length === 0 && <p className="text-xs text-muted-foreground p-3">No cast or crew on this film yet.</p>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="size-4 text-primary" /> Send call sheet</DialogTitle>
            <DialogDescription>
              {withEmail.length} of {included.length} people have an email and get the link now. The other {included.length - withEmail.length} get a private link you can forward on WhatsApp.
              {sheet.status === 'sent' && ' Re-sending keeps everyone\'s confirmation.'}
            </DialogDescription>
          </DialogHeader>
          {!sendResult ? (
            <>
              <Field label="Message (optional)"><Textarea value={sendMsg} onChange={(e) => setSendMsg(e.target.value)} rows={2} placeholder="e.g. Rain expected — bring rain cover for gear." /></Field>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sending}>Cancel</Button>
                <Button onClick={send} disabled={sending} data-testid="cs-send-confirm">{sending ? <Loader2 className="size-4 mr-1 animate-spin" /> : <Send className="size-4 mr-1" />} Send to {included.length}</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <p className="text-sm">Emailed {sendResult.sent}{sendResult.failed ? `, ${sendResult.failed} failed` : ''}{sendResult.links_only ? `, ${sendResult.links_only} link-only` : ''}.</p>
              {sendResult.results.some((r) => r.channel === 'link') && (
                <ul className="text-xs divide-y divide-border max-h-48 overflow-y-auto">
                  {sendResult.results.filter((r) => r.channel === 'link').map((r) => (
                    <li key={r.recipient_id} className="py-1.5 flex items-center gap-2"><span className="w-32 truncate font-medium">{r.name}</span><code className="flex-1 truncate text-muted-foreground">{r.url}</code>
                      <button type="button" onClick={() => navigator.clipboard?.writeText(r.url).then(() => toast.success('Copied'))}><Copy className="size-3.5" /></button></li>
                  ))}
                </ul>
              )}
              <DialogFooter><Button onClick={() => setSendOpen(false)}>Done</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Read-only rendering of the view-model — what the crew sees. */
export function PreviewPane({ view, compact }) {
  const tone = { white: 'border-l-zinc-400', yellow: 'border-l-yellow-400', blue: 'border-l-blue-500', green: 'border-l-green-500', neutral: 'border-l-muted-foreground/40' };
  return (
    <div className="rounded-2xl border border-border bg-card p-4 md:p-6 space-y-5" data-testid="cs-preview">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Call sheet{view.version > 1 ? ` · v${view.version}` : ''} · {view.film_title}</div>
        <h2 className="text-xl font-bold tracking-tight">{view.title}</h2>
        <p className="text-sm text-muted-foreground">{view.shoot_date_text || 'Date to be confirmed'}{view.location_name ? ` · ${view.location_name}` : ''}</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[['General call', view.general_call, true], ['Crew call', view.crew_call], ['Shooting call', view.shooting_call], ['Est. wrap', view.est_wrap]].map(([l, v, dark]) => (
          <div key={l} className={cn('rounded-xl px-3 py-2', dark ? 'bg-foreground text-background' : 'bg-muted')}><div className="text-lg font-bold leading-tight">{v || '—'}</div><div className="text-[10px] uppercase tracking-wider opacity-70">{l}</div></div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Location</div><div className="font-semibold">{view.location_name || '—'}</div>{view.location_address && <div className="text-xs text-muted-foreground">{view.location_address}</div>}{view.map_url && <a href={view.map_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline">Open map</a>}{view.parking_notes && <div className="text-xs text-muted-foreground">Parking / unit base: {view.parking_notes}</div>}</div>
        <div className="space-y-3">
          <div className="rounded-xl border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Nearest hospital</div><div>{view.nearest_hospital || '—'}</div></div>
          <div className="rounded-xl border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Weather & light</div><div>{view.weather || '—'}{(view.sunrise || view.sunset) && <span className="text-xs text-muted-foreground"> · ↑ {view.sunrise || '—'} ↓ {view.sunset || '—'}</span>}</div></div>
        </div>
      </div>
      {view.contacts?.length > 0 && <div className="text-sm"><span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-2">Contacts</span>{view.contacts.map((c, i) => <span key={i} className="mr-3"><b>{c.name}</b>{c.role ? ` (${c.role})` : ''}{c.phone ? ` · ${c.phone}` : ''}</span>)}</div>}
      {view.notes && <div className="rounded-xl bg-muted p-3 text-sm whitespace-pre-wrap">{view.notes}</div>}
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Scenes in shooting order</div>
        <div className="space-y-1">
          {view.scenes.map((sc) => (
            <div key={sc.number} className={cn('flex items-start gap-3 rounded-md border border-border border-l-4 px-3 py-2 text-sm', tone[sc.colour] || tone.neutral)}>
              <span className="font-bold w-8">{sc.number}</span>
              <div className="min-w-0 flex-1"><div className="font-medium">{sc.slug}</div>{sc.location && sc.location !== sc.slug && <div className="text-xs text-muted-foreground">{sc.location}</div>}{sc.summary && <div className="text-xs text-muted-foreground">{sc.summary}</div>}</div>
              <span className="text-[10px] font-bold text-muted-foreground w-16">{sc.ie_tod}</span>
              {!compact && <span className="text-xs text-muted-foreground w-40 truncate">{sc.cast.join(', ')}</span>}
            </div>
          ))}
          {view.scenes.length === 0 && <p className="text-xs text-muted-foreground italic">No scenes on this day.</p>}
        </div>
      </div>
      {Object.entries(view.groups).map(([dept, people]) => (
        <div key={dept}>
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1">{dept}</div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-border">
              {people.map((p, i) => (
                <tr key={i} className={cn(p.me && 'bg-primary/5')}>
                  <td className="py-1.5 pr-3 font-bold w-16">{p.call_time || view.general_call || '—'}</td>
                  <td className="py-1.5 pr-3 text-xs text-muted-foreground w-14">{p.pickup_time || ''}</td>
                  <td className="py-1.5 pr-3"><b>{p.name}</b>{p.me && <span className="text-[10px] ml-1 text-primary font-semibold">YOU</span>}{p.role && <span className="text-xs text-muted-foreground"> · {p.role}</span>}</td>
                  <td className="py-1.5 text-xs text-muted-foreground">{p.scenes?.length ? `Sc ${p.scenes.join(', ')}` : ''}{p.phone ? ` ${p.phone}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {view.advance_notes && <div><div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Advance — next day</div><div className="rounded-xl bg-muted p-3 text-sm whitespace-pre-wrap">{view.advance_notes}</div></div>}
    </div>
  );
}
